import type { ChatMessage, SessionFile, SessionState, SessionStatus, EntitlementSnapshot } from '../types/session.types';

export interface SessionProviderState {
  activeSessions: Map<string, SessionState>;
  currentSessionId: string | null;
}

export type SessionAction =
  | { type: 'CREATE_SESSION'; payload: { sessionId: string; agentName: string; maxTurns: number; entitlementSnapshot?: EntitlementSnapshot } }
  | { type: 'SWITCH_SESSION'; payload: { sessionId: string } }
  | { type: 'TERMINATE_SESSION'; payload: { sessionId: string } }
  | { type: 'TERMINATE_ALL' }
  | { type: 'UPDATE_STATUS'; payload: { sessionId: string; status: SessionStatus; turnCount?: number; maxTurns?: number } }
  | { type: 'ADD_MESSAGE'; payload: { sessionId: string; message: ChatMessage } }
  | { type: 'ADD_FILE'; payload: { sessionId: string; file: SessionFile } }
  | { type: 'UPDATE_DRAFT'; payload: { sessionId: string; draft: string } }
  | { type: 'INCREMENT_TURN'; payload: { sessionId: string } }
  | { type: 'ADD_DELTA'; payload: { sessionId: string; delta: string } }
  | { type: 'SET_THINKING'; payload: { sessionId: string; thinking: boolean } }
  | { type: 'ADD_ARTIFACT'; payload: { sessionId: string; file: SessionFile } }
  | { type: 'REMOVE_ARTIFACT'; payload: { sessionId: string; fileId: string } }
  | { type: 'SET_ERROR'; payload: { sessionId: string; message: string } }
  | { type: 'RESET_SESSION'; payload: { sessionId: string } }
  | { type: 'ADD_FILES'; payload: { sessionId: string; files: SessionFile[] } }
  | { type: 'SET_MESSAGES'; payload: { sessionId: string; messages: ChatMessage[] } }
  | { type: 'SET_ACTIVE_PERMISSION'; payload: { sessionId: string; permission: { permissionId: string; operation: string; reason: string } | null } }
  | { type: 'SET_ACTIVE_QUESTION'; payload: { sessionId: string; question: { questionId: string; question: string; quickReplies: string[] } | null } };

export const initialState: SessionProviderState = {
  activeSessions: new Map(),
  currentSessionId: null,
};

export function sessionReducer(state: SessionProviderState, action: SessionAction): SessionProviderState {
  switch (action.type) {
    case 'CREATE_SESSION': {
      if (state.activeSessions.size >= 3) {
        return state; // Enforce 3-session limit
      }
      const newSession: SessionState = {
        sessionId: action.payload.sessionId,
        agentName: action.payload.agentName,
        status: 'idle',
        messages: [],
        generatedFiles: [],
        turnCount: 0,
        maxTurns: action.payload.maxTurns,
        startedAt: new Date().toISOString(),
        elapsedSeconds: 0,
        unreadCount: 0,
        draft: '',
        entitlementSnapshot: action.payload.entitlementSnapshot,
      };
      const nextSessions = new Map(state.activeSessions);
      nextSessions.set(newSession.sessionId, newSession);
      return {
        ...state,
        activeSessions: nextSessions,
        currentSessionId: newSession.sessionId,
      };
    }
    
    case 'SWITCH_SESSION': {
      const sessionId = action.payload.sessionId;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      const session = nextSessions.get(sessionId)!;
      nextSessions.set(sessionId, { ...session, unreadCount: 0 }); // Reset unread count

      return {
        ...state,
        activeSessions: nextSessions,
        currentSessionId: sessionId,
      };
    }

    case 'TERMINATE_SESSION': {
      const sessionId = action.payload.sessionId;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      nextSessions.delete(sessionId);

      let nextCurrentSessionId = state.currentSessionId;
      if (state.currentSessionId === sessionId) {
        // If we closed the active tab, switch to the last remaining one, or null
        const remainingIds = Array.from(nextSessions.keys());
        nextCurrentSessionId = remainingIds.length > 0 ? remainingIds[remainingIds.length - 1] : null;
      }

      return {
        ...state,
        activeSessions: nextSessions,
        currentSessionId: nextCurrentSessionId,
      };
    }

    case 'TERMINATE_ALL': {
      return {
        ...state,
        activeSessions: new Map(),
        currentSessionId: null,
      };
    }

    case 'UPDATE_STATUS': {
      const { sessionId, status, maxTurns } = action.payload;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      const session = nextSessions.get(sessionId)!;
      const newTurnCount = session.turnCount;
      const newMaxTurns = maxTurns ?? session.maxTurns;
      const isComplete = newTurnCount >= newMaxTurns;

      nextSessions.set(sessionId, {
        ...session,
        status: isComplete && status !== 'summarizing' ? 'complete' : status,
        turnCount: newTurnCount,
        maxTurns: newMaxTurns,
        // Clear error when transitioning back to busy state
        error: status === 'busy' ? undefined : session.error,
      });

      return { ...state, activeSessions: nextSessions };
    }

    case 'ADD_MESSAGE': {
      const { sessionId, message } = action.payload;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      const session = nextSessions.get(sessionId)!;
      
      const isBackground = state.currentSessionId !== sessionId;
      const nextTurnCount = message.role === 'user' ? session.turnCount + 1 : session.turnCount;
      
      nextSessions.set(sessionId, { 
        ...session, 
        messages: [...session.messages, message],
        unreadCount: isBackground ? session.unreadCount + 1 : session.unreadCount,
        turnCount: nextTurnCount,
        // Reset error on user message submission so the UI gets unstuck/cleared
        error: undefined,
        status: message.role === 'user' ? 'busy' : session.status,
      });

      return { ...state, activeSessions: nextSessions };
    }

    case 'ADD_FILE': {
      const { sessionId, file } = action.payload;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      const session = nextSessions.get(sessionId)!;
      
      // Ensure no duplicates by fileId
      if (session.generatedFiles.some(f => f.fileId === file.fileId)) return state;

      nextSessions.set(sessionId, { 
        ...session, 
        generatedFiles: [...session.generatedFiles, file] 
      });

      return { ...state, activeSessions: nextSessions };
    }

    case 'UPDATE_DRAFT': {
      const { sessionId, draft } = action.payload;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      const session = nextSessions.get(sessionId)!;
      nextSessions.set(sessionId, { ...session, draft });

      return { ...state, activeSessions: nextSessions };
    }

    case 'INCREMENT_TURN': {
      const { sessionId } = action.payload;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      const session = nextSessions.get(sessionId)!;
      nextSessions.set(sessionId, { ...session, turnCount: session.turnCount + 1 });

      return { ...state, activeSessions: nextSessions };
    }

    case 'ADD_DELTA': {
      const { sessionId, delta } = action.payload;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      const session = nextSessions.get(sessionId)!;
      const lastMessage = session.messages[session.messages.length - 1];

      if (lastMessage && lastMessage.role === 'assistant') {
        // Append to existing assistant message
        const updatedMessages = [...session.messages];
        updatedMessages[updatedMessages.length - 1] = {
          ...lastMessage,
          content: lastMessage.content + delta,
        };
        nextSessions.set(sessionId, { ...session, messages: updatedMessages });
      } else {
        // Create new assistant message
        const newMessage: ChatMessage = {
          id: `delta-${Date.now()}`,
          role: 'assistant',
          content: delta,
          timestamp: new Date().toISOString(),
          turnIndex: session.messages.length,
        };
        nextSessions.set(sessionId, {
          ...session,
          messages: [...session.messages, newMessage],
        });
      }

      return { ...state, activeSessions: nextSessions };
    }

    case 'SET_THINKING': {
      const { sessionId, thinking } = action.payload;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      const session = nextSessions.get(sessionId)!;
      nextSessions.set(sessionId, {
        ...session,
        status: thinking ? 'busy' : 'idle',
      });

      return { ...state, activeSessions: nextSessions };
    }

    case 'ADD_ARTIFACT': {
      const { sessionId, file } = action.payload;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      const session = nextSessions.get(sessionId)!;

      if (session.generatedFiles.some((f) => f.fileId === file.fileId)) return state;

      nextSessions.set(sessionId, {
        ...session,
        generatedFiles: [...session.generatedFiles, file],
      });

      return { ...state, activeSessions: nextSessions };
    }

    case 'REMOVE_ARTIFACT': {
      const { sessionId, fileId } = action.payload;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      const session = nextSessions.get(sessionId)!;
      nextSessions.set(sessionId, {
        ...session,
        generatedFiles: session.generatedFiles.filter((f) => f.fileId !== fileId),
      });

      return { ...state, activeSessions: nextSessions };
    }

    case 'SET_ERROR': {
      const { sessionId, message } = action.payload;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      const session = nextSessions.get(sessionId)!;
      nextSessions.set(sessionId, { ...session, status: 'error', error: { message } });

      return { ...state, activeSessions: nextSessions };
    }

    case 'RESET_SESSION': {
      const { sessionId } = action.payload;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      const session = nextSessions.get(sessionId)!;
      nextSessions.set(sessionId, {
        ...session,
        messages: [],
        generatedFiles: [],
        turnCount: 0,
        status: 'idle',
        draft: '',
      });

      return { ...state, activeSessions: nextSessions };
    }

    case 'ADD_FILES': {
      const { sessionId, files } = action.payload;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      const session = nextSessions.get(sessionId)!;
      nextSessions.set(sessionId, {
        ...session,
        generatedFiles: [...session.generatedFiles, ...files],
      });

      return { ...state, activeSessions: nextSessions };
    }

    case 'SET_MESSAGES': {
      const { sessionId, messages } = action.payload;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      const session = nextSessions.get(sessionId)!;
      const turnCount = messages.filter((m) => m.role === 'user').length;
      nextSessions.set(sessionId, { ...session, messages, turnCount });

      return { ...state, activeSessions: nextSessions };
    }

    case 'SET_ACTIVE_PERMISSION': {
      const { sessionId, permission } = action.payload;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      const session = nextSessions.get(sessionId)!;
      nextSessions.set(sessionId, { 
        ...session, 
        activePermission: permission || undefined 
      });

      return { ...state, activeSessions: nextSessions };
    }

    case 'SET_ACTIVE_QUESTION': {
      const { sessionId, question } = action.payload;
      if (!state.activeSessions.has(sessionId)) return state;

      const nextSessions = new Map(state.activeSessions);
      const session = nextSessions.get(sessionId)!;
      nextSessions.set(sessionId, { 
        ...session, 
        activeQuestion: question || undefined 
      });

      return { ...state, activeSessions: nextSessions };
    }

    default:
      return state;
  }
}
